import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Spin,
  Badge,
  DatePicker,
  Space,
  Flex,
  Tooltip,
} from "antd";
import {
  ShoppingCartOutlined,
  UserAddOutlined,
  RiseOutlined,
  DollarOutlined,
  SearchOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import bdmAnalyticsService from "../../services/bdmAnalyticsService";
import {
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

// Add CSS for table cells
const tableStyle = `
  .centered-table .ant-table-cell {
    text-align: center !important;
  }
  .centered-table .ant-table-cell:first-child {
    text-align: left !important;
  }
`;

const UserAnalytics = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(3);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);

  // Helper function to format numbers with commas (Indian numbering system)
  const formatNumber = (num) => {
    if (!num && num !== 0) return "0";
    // Round to 2 decimal places first -- live currency conversion produces
    // floats like 90675.36000000002, and without this the raw string form
    // of that float leaks straight through to the UI.
    const rounded = Math.round(Number(num) * 100) / 100;
    const numStr = String(rounded);
    const [integer, decimal] = numStr.split(".");

    // Indian numbering system: last 3 digits, then groups of 2
    const lastThree = integer.slice(-3);
    const otherNumbers = integer.slice(0, -3);
    const formatted =
      otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") +
      (otherNumbers ? "," : "") +
      lastThree;

    return decimal ? `${formatted}.${decimal.padEnd(2, "0")}` : formatted;
  };

  const [expanded, setExpanded] = useState({}); // track expanded cards
  const [loadingExpand, setLoadingExpand] = useState({}); // track loading per card

  // Get sidebar collapsed state from Redux
  const sidebarCollapsed = useSelector((state) => state.ui.collapsed);

  const cardWidth = 460; // must match your card's maxWidth

  // unwrap hierarchy safely
  let hierarchyToRender = [];
  if (analyticsData?.hierarchy?.length) {
    if (analyticsData.hierarchy[0]?.hierarchy?.length) {
      hierarchyToRender = analyticsData.hierarchy;
    } else {
      hierarchyToRender = analyticsData.hierarchy;
    }
  }

  // fetch analytics (optionally for a parent)
  const fetchUserAnalyticsData = async (focusUid = null) => {
    setLoading(true);
    const startDate = dateRange[0]
      ? dayjs(dateRange[0]).format("YYYY-MM-DD")
      : null;
    const endDate = dateRange[1]
      ? dayjs(dateRange[1]).format("YYYY-MM-DD")
      : null;

    const response = await bdmAnalyticsService.fetchUserAnalytics(
      startDate,
      endDate,
      null,
      null,
      [],
      focusUid,
    );
    if (response.success) {
      console.log(response.data);
      setAnalyticsData(response.data);
      setCurrentIndex(0); // reset carousel
    } else {
      console.error("Failed to fetch User Analytics data");
    }
    setLoading(false);
  };

  const handleSearch = () => {
    // Close all expanded cards when search is clicked
    setExpanded({});
    fetchUserAnalyticsData();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // Check if date range is selected
  const isDateRangeSelected = () => {
    return dateRange && dateRange[0] !== null && dateRange[1] !== null;
  };

  useEffect(() => {
    fetchUserAnalyticsData(); // initial load
  }, []);

  // resize handling
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      let show = mobile ? 1 : 3;
      if (hierarchyToRender.length && show > hierarchyToRender.length) {
        show = hierarchyToRender.length;
      }
      setCardsToShow(show);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hierarchyToRender]);

  const columns = [
    {
      title: "Metric",
      dataIndex: "metric",
      key: "metric",
      align: "left",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#F3F4F6",
          fontWeight: 600,
          color: "#161415",
          textAlign: "left",
        },
      }),
    },
    {
      title: "Today",
      dataIndex: "today",
      key: "today",
      align: "center",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#F3F4F6",
          fontWeight: 600,
          color: "#161415",
          textAlign: "center",
        },
      }),
    },
    {
      title: "Yesterday",
      dataIndex: "yesterday",
      key: "yesterday",
      align: "center",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#F3F4F6",
          fontWeight: 600,
          color: "#161415",
          textAlign: "center",
        },
      }),
    },
    {
      title: "This Month",
      dataIndex: "month",
      key: "month",
      width: 140,
      align: "center",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#F3F4F6",
          fontWeight: 600,
          color: "#161415",
          textAlign: "center",
        },
      }),
    },
  ];

  const CustomCard = ({
    title,
    value,
    icon,
    bgColor,
    comparison,
    customTitle,
    description,
  }) => {
    // Calculate percentage change if comparison exists
    let percentageChange = null;
    let comparisonText = "";

    // Use custom title if provided (for date range mode)
    const displayTitle = customTitle || title;
    let comparisonColor = "green";

    if (comparison !== undefined && comparison !== null) {
      // Get yesterday's value (if comparison is a difference)
      const yesterdayValue =
        typeof value === "string" && value.includes("₹")
          ? parseFloat(value.replace("₹", "").replace(/,/g, "")) - comparison
          : parseFloat(String(value).replace(/,/g, "")) - comparison;

      // Calculate percentage only if yesterday's value is not zero to avoid division by zero
      if (yesterdayValue !== 0) {
        percentageChange = ((comparison / yesterdayValue) * 100).toFixed(1);
        comparisonText = `${percentageChange}% vs yesterday`;
      } else if (comparison > 0) {
        // If yesterday was zero but today has value
        comparisonText = "New activity today";
      }

      // Determine color based on comparison (green for positive, red for negative)
      comparisonColor = comparison >= 0 ? "green" : "red";
    }

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#fff",
          padding: "16px 18px",
          borderRadius: "10px",
          width: "100%",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          marginBottom: isMobile ? "12px" : "0",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: "flex-start",
            flex: 1,
            minWidth: 0,
            marginRight: "10px",
          }}
        >
          <h4
            style={{
              fontSize: "12px",
              color: "grey",
              fontWeight: "400",
              margin: 0,
              wordBreak: "break-word",
              overflowWrap: "break-word",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {title}
            {description && (
              <Tooltip title={description}>
                <InfoCircleOutlined
                  style={{ fontSize: "11px", color: "#bbb", cursor: "help" }}
                />
              </Tooltip>
            )}
          </h4>
          <p
            style={{
              fontSize: "17px",
              fontWeight: "600",
              margin: 0,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {value}
          </p>
          {comparisonText && (
            <p style={{ fontSize: "11px", color: comparisonColor, margin: 0 }}>
              {comparisonText}
            </p>
          )}
        </div>
        <div style={{ flexShrink: 0 }}>{icon}</div>
      </div>
    );
  };

  // expand / collapse toggle with fetch
  const toggleExpand = async (uid) => {
    // collapse if already expanded
    if (expanded[uid]) {
      // Create a new expanded state without the current uid
      const newExpanded = { ...expanded };
      delete newExpanded[uid];
      setExpanded(newExpanded);
      return;
    }

    // Set loading state for this card only
    setLoadingExpand((prev) => ({ ...prev, [uid]: true }));

    // Get current date range values
    const startDate = dateRange[0]
      ? dayjs(dateRange[0]).format("YYYY-MM-DD")
      : null;
    const endDate = dateRange[1]
      ? dayjs(dateRange[1]).format("YYYY-MM-DD")
      : null;

    try {
      const response = await bdmAnalyticsService.fetchUserAnalytics(
        startDate,
        endDate,
        null,
        null,
        [],
        uid,
        true, // children_only=true parameter
      );

      if (response.success) {
        console.log("response.data", response.data);
        // Store the response data for this card while preserving other expanded cards
        setExpanded((prev) => ({
          ...prev,
          [uid]: response.data,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch team performance", err);
    }

    // Clear loading state for this card only
    setLoadingExpand((prev) => {
      const newLoadingExpand = { ...prev };
      delete newLoadingExpand[uid];
      return newLoadingExpand;
    });
  };

  // render team members from expanded data
  const renderSubTeam = (data) => {
    // Handle the new array response format when children_only=true
    // If data is an array, use it directly; otherwise, try to access data.hierarchy
    const teamMembers = Array.isArray(data) ? data : data?.hierarchy || [];

    if (teamMembers.length === 0) return null;

    return teamMembers.map((member) => (
      <Card
        key={member.uid}
        type="inner"
        style={{
          marginTop: 16,
          width: "100%",
          maxWidth: "430px",
        }}
        title={
          <div
            style={{
              padding: "10px 0px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <h4 style={{ margin: 0 }}>{member.user}</h4>
            <p style={{ margin: 0, color: "grey", fontSize: "12px" }}>
              {member.user_type}
            </p>
            <p
              style={{
                margin: 0,
                color: "#36c6d3",
                fontSize: "12px",
                fontWeight: 200,
              }}
            >
              {member?.member_count}{" "}
              {member?.member_count <= 1 ? "Team Member" : "Team Members"}
            </p>
          </div>
        }
      >
        {isDateRangeSelected() ? (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12}>
                <div style={{ background: "#f6ffed", padding: "6px" }}>
                  <h4 style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}>
                    Revenue
                  </h4>
                  <strong style={{ fontWeight: 600, color: "#36c6d3" }}>
                    ₹
                    {formatNumber(
                      member?.self_stats?.custom_range?.transaction_sum || 0,
                    )}
                  </strong>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ background: "#e6f7ff", padding: "6px" }}>
                  <h4 style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}>
                    Orders
                  </h4>
                  <strong style={{ fontWeight: 600, color: "#1C64F2" }}>
                    {formatNumber(
                      member?.self_stats?.custom_range?.task_count || 0,
                    )}
                  </strong>
                </div>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12}>
                <div style={{ background: "#fff7e6", padding: "6px" }}>
                  <h4 style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}>
                    New Customers
                  </h4>
                  <strong style={{ fontWeight: 600, color: "#0E9F6E" }}>
                    {formatNumber(
                      member?.self_stats?.custom_range?.client_count || 0,
                    )}
                  </strong>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ background: "#f9f0ff", padding: "6px" }}>
                  <h4 style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}>
                    New Customer Revenue
                  </h4>
                  <strong style={{ fontWeight: 600, color: "#722ED1" }}>
                    ₹
                    {formatNumber(
                      member?.self_stats?.custom_range
                        ?.transaction_sum_clients_created_in_range || 0,
                    )}
                  </strong>
                </div>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12}>
                <div style={{ background: "#CDDBFE", padding: "6px" }}>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      fontWeight: 300,
                    }}
                  >
                    Word Count
                  </h4>
                  <strong style={{ fontWeight: 600, color: "#1C64F2" }}>
                    {formatNumber(
                      member?.self_stats?.custom_range?.word_count || 0,
                    )}
                  </strong>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ background: "#fff7e6", padding: "6px" }}>
                  <h4 style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}>
                    Call Connected / Made
                  </h4>
                  <strong style={{ fontWeight: 600, color: "#0E9F6E" }}>
                    {formatNumber(
                      member?.self_stats?.custom_range?.call_connected || 0,
                    )} /{" "}
                    {formatNumber(
                      member?.self_stats?.custom_range?.call_made || 0,
                    )}
                  </strong>
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          <Table
            size="small"
            pagination={false}
            columns={columns}
            className="centered-table"
            dataSource={[
              {
                key: "1",
                metric: (
                  <span
                    style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                  >
                    Revenue
                  </span>
                ),
                today: (
                  <span>{`₹${formatNumber(
                    member?.self_stats?.today?.transaction_sum || 0,
                  )}`}</span>
                ),
                yesterday: (
                  <span>{`₹${formatNumber(
                    member?.self_stats?.yesterday?.transaction_sum || 0,
                  )}`}</span>
                ),
                month: (
                  <span
                    style={{ fontWeight: 600, color: "#6875F5" }}
                  >{`₹${formatNumber(
                    member?.self_stats?.this_month?.transaction_sum || 0,
                  )}`}</span>
                ),
              },

              {
                key: "2",
                metric: (
                  <span
                    style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                  >
                    Orders
                  </span>
                ),
                today: (
                  <span>
                    {formatNumber(member?.self_stats?.today?.task_count || 0)}
                  </span>
                ),
                yesterday: (
                  <span>
                    {formatNumber(
                      member?.self_stats?.yesterday?.task_count || 0,
                    )}
                  </span>
                ),
                month: (
                  <span style={{ fontWeight: 600, color: "#6875F5" }}>
                    {formatNumber(
                      member?.self_stats?.this_month?.task_count || 0,
                    )}
                  </span>
                ),
              },
              {
                key: "3",
                metric: (
                  <span
                    style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                  >
                    New Customers
                  </span>
                ),
                today: (
                  <span>
                    {formatNumber(member?.self_stats?.today?.client_count || 0)}
                  </span>
                ),
                yesterday: (
                  <span>
                    {formatNumber(
                      member?.self_stats?.yesterday?.client_count || 0,
                    )}
                  </span>
                ),
                month: (
                  <span style={{ fontWeight: 600, color: "#6875F5" }}>
                    {formatNumber(
                      member?.self_stats?.this_month?.client_count || 0,
                    )}
                  </span>
                ),
              },
              {
                key: "4",
                metric: (
                  <span
                    style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                  >
                    New Customer Revenue
                  </span>
                ),
                today: (
                  <span>
                    ₹
                    {formatNumber(
                      member?.self_stats?.today
                        ?.transaction_sum_clients_created_in_range || 0,
                    )}
                  </span>
                ),
                yesterday: (
                  <span>
                    ₹
                    {formatNumber(
                      member?.self_stats?.yesterday
                        ?.transaction_sum_clients_created_in_range || 0,
                    )}
                  </span>
                ),
                month: (
                  <span style={{ fontWeight: 600, color: "#6875F5" }}>
                    ₹
                    {formatNumber(
                      member?.self_stats?.this_month
                        ?.transaction_sum_clients_created_in_range || 0,
                    )}
                  </span>
                ),
              },
              {
                key: "5",
                metric: (
                  <span
                    style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                  >
                    Word Count
                  </span>
                ),
                today: (
                  <span>
                    {formatNumber(member?.self_stats?.today?.word_count || 0)}
                  </span>
                ),
                yesterday: (
                  <span>
                    {formatNumber(
                      member?.self_stats?.yesterday?.word_count || 0,
                    )}
                  </span>
                ),
                month: (
                  <span style={{ fontWeight: 600, color: "#6875F5" }}>
                    {formatNumber(
                      member?.self_stats?.this_month?.word_count || 0,
                    )}
                  </span>
                ),
              },
              {
                key: "6",
                metric: (
                  <span
                    style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                  >
                    Call Made
                  </span>
                ),
                today: (
                  <span>
                    {formatNumber(member?.self_stats?.today?.call_made || 0)}
                  </span>
                ),
                yesterday: (
                  <span>
                    {formatNumber(
                      member?.self_stats?.yesterday?.call_made || 0,
                    )}
                  </span>
                ),
                month: (
                  <span style={{ fontWeight: 600, color: "#6875F5" }}>
                    {formatNumber(
                      member?.self_stats?.this_month?.call_made || 0,
                    )}
                  </span>
                ),
              },
              {
                key: "7",
                metric: (
                  <span
                    style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                  >
                    Call Connected
                  </span>
                ),
                today: (
                  <span>
                    {formatNumber(member?.self_stats?.today?.call_connected || 0)}
                  </span>
                ),
                yesterday: (
                  <span>
                    {formatNumber(
                      member?.self_stats?.yesterday?.call_connected || 0,
                    )}
                  </span>
                ),
                month: (
                  <span style={{ fontWeight: 600, color: "#6875F5" }}>
                    {formatNumber(
                      member?.self_stats?.this_month?.call_connected || 0,
                    )}
                  </span>
                ),
              },
            ]}
          />
        )}
        {member.has_more && (
          <Button
            type="primary"
            style={{
              marginTop: 10,
              width: "100%",
              background: expanded[member.uid] ? "#e6f7ff" : "#36c6d3",
              border: "none",
              color: expanded[member.uid] ? "#36c6d3" : "white",
            }}
            onClick={() => toggleExpand(member.uid)}
            loading={loadingExpand[member.uid]}
            disabled={loadingExpand[member.uid]}
          >
            {expanded[member.uid] ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                Hide Team Performance{" "}
                <ChevronUp size={16} style={{ marginLeft: "5px" }} />
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center" }}>
                View Team Performance{" "}
                <ChevronDown size={16} style={{ marginLeft: "5px" }} />
              </div>
            )}
          </Button>
        )}
        {/* Show child's data only when expanded */}
        {expanded[member.uid] && (
          <div style={{ marginTop: 16 }}>
            {loadingExpand[member.uid] ? (
              <Spin />
            ) : (
              // Check if expanded data exists
              expanded[member.uid] && (
                <div
                  style={{
                    paddingLeft: "10px",
                    marginLeft: "10px",
                  }}
                >
                  <div style={{ marginBottom: 12 }}>
                    <strong style={{ fontWeight: 600, color: "#1C64F2" }}>
                      Order Count:{" "}
                      {formatNumber(
                        (Array.isArray(expanded[member.uid])
                          ? expanded[member.uid]
                          : expanded[member.uid]?.hierarchy || []
                        ).reduce((sum, child) => {
                          const count = isDateRangeSelected()
                            ? child?.self_stats?.custom_range?.task_count
                            : child?.self_stats?.this_month?.task_count;
                          return sum + (Number(count) || 0);
                        }, 0),
                      )}
                    </strong>
                  </div>
                  {renderSubTeam(expanded[member.uid])}
                </div>
              )
            )}
          </div>
        )}
      </Card>
    ));
  };

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          width: "75vw",
        }}
      >
        <Spin />
      </div>
    );
  if (!analyticsData) return <p>No data available</p>;

  const { overall_stats } = analyticsData;

  return (
    <div>
      <style>{tableStyle}</style>

      {/* Filters */}
      <div
        style={{
          background: "#eceff1",
          padding: "20px",
          borderRadius: "8px",
          width: isMobile ? "max-content" : sidebarCollapsed ? "88%" : "79.5%",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "end" : "center",
          gap: "20px",
        }}
      >
        <Space direction="vertical" size={12}>
          <DatePicker.RangePicker
            format="DD-MM-YYYY"
            style={{ width: 280 }}
            value={dateRange}
            allowClear
            onChange={handleDateRangeChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && dateRange[0] && dateRange[1]) {
                handleSearch();
              }
            }}
            placeholder={["Start Date", "End Date"]}
          />
        </Space>
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            style={{ background: "#040724" }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              setDateRange([null, null]);
              setExpanded({});
              fetchUserAnalyticsData();
            }}
          >
            Reset
          </Button>
        </Space>
      </div>

      {/* Top KPIs */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <div
          style={{
            padding: "0 4px",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(4, minmax(0, 1fr))",
            gap: "14px",
            paddingBottom: "10px",
            width: isMobile ? "max-content" : sidebarCollapsed ? "88%" : "79.5%",
          }}
        >
          <CustomCard
            title={
              isDateRangeSelected() ? "Total Revenue" : "Total Revenue Today"
            }
            value={`₹${formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.transaction_sum || 0
                : overall_stats?.today?.transaction_sum || 0,
            )}`}
            icon={
              <DollarOutlined style={{ fontSize: "26px", color: "#36c6d3" }} />
            }
            bgColor="#36c6d3"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.transaction_sum || 0
            }
            description="Sum of payment transactions recorded today, excluding wallet top-ups (wallet refunds are included). Reflects money actually transacted, not order value."
          />
          <CustomCard
            title={
              isDateRangeSelected() ? "Total Orders" : "Total Orders Today"
            }
            value={formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.task_count || 0
                : overall_stats?.today?.task_count || 0,
            )}
            icon={
              <ShoppingCartOutlined
                style={{ fontSize: "26px", color: "#36c6d3" }}
              />
            }
            bgColor="#ed6b75"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.task_count || 0
            }
            description="Count of every order created today, regardless of status — includes unassigned and cancelled orders."
          />
          <CustomCard
            title={
              isDateRangeSelected()
                ? "New Client Confirmed Orders"
                : "New Client Confirmed Orders Today"
            }
            value={formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.first_time_buyer_count || 0
                : overall_stats?.today?.first_time_buyer_count || 0,
            )}
            icon={
              <UserAddOutlined style={{ fontSize: "26px", color: "#36c6d3" }} />
            }
            bgColor="#F1C40F"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.first_time_buyer_count || 0
            }
            description="Distinct clients placing their first-ever confirmed order in this period — no prior confirmed order, regardless of how long ago they signed up. New signups who order today are automatically included here too."
          />
          <CustomCard
            title={
              isDateRangeSelected()
                ? "New Client Order Budget"
                : "New Client Order Budget Today"
            }
            value={`₹${formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.new_client_order_budget_sum || 0
                : overall_stats?.today?.new_client_order_budget_sum || 0,
            )}`}
            icon={
              <DollarOutlined style={{ fontSize: "26px", color: "#36c6d3" }} />
            }
            bgColor="#36c6d3"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.new_client_order_budget_sum || 0
            }
            description="Quoted budget of confirmed orders placed today by first-time buyers — same client cohort as the 'New Client Confirmed Orders' box."
          />
          <CustomCard
            title="New Customer Revenue"
            value={`₹${formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range
                  ?.transaction_sum_clients_created_in_range || 0
                : overall_stats?.today
                  ?.transaction_sum_clients_created_in_range || 0,
            )}`}
            icon={
              <RiseOutlined style={{ fontSize: "26px", color: "#36c6d3" }} />
            }
            bgColor="#fefefe"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday
                  ?.transaction_sum_clients_created_in_range || 0
            }
            description="Amount actually received (payment collected, not just quoted budget) against orders placed by first-time buyers in this period — same client cohort as the 'New Client Confirmed Orders' box."
          />

          <CustomCard
            title={isDateRangeSelected() ? "Word Count" : "Today's Word Count"}
            value={formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.word_count || 0
                : overall_stats?.today?.word_count || 0,
            )}
            icon={
              <UserAddOutlined style={{ fontSize: "26px", color: "#36c6d3" }} />
            }
            bgColor="#F1C40F"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.client_count || 0
            }
            description="Total internal word count (word_internal) summed across every order created today, regardless of status — includes unassigned and cancelled orders."
          />

          <CustomCard
            title={
              isDateRangeSelected()
                ? "Confirmed Word Count"
                : "Today's Confirmed Word Count"
            }
            value={formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.confirmed_word_count || 0
                : overall_stats?.today?.confirmed_word_count || 0,
            )}
            icon={
              <UserAddOutlined style={{ fontSize: "26px", color: "#36c6d3" }} />
            }
            bgColor="#27AE60"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.client_count || 0
            }
            description="Same as Word Count, but excludes Unassigned and Cancelled orders — so a corrupted or junk word count on a dead order can't inflate this figure."
          />
          <CustomCard
            title={
              isDateRangeSelected()
                ? "Total Order Budget"
                : "Total Order Budget Today"
            }
            value={`₹${formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.order_budget_sum || 0
                : overall_stats?.today?.order_budget_sum || 0,
            )}`}
            icon={
              <DollarOutlined style={{ fontSize: "26px", color: "#36c6d3" }} />
            }
            bgColor="#36c6d3"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.order_budget_sum || 0
            }
            description="Total quoted budget of orders created today, counting only confirmed orders (unassigned and cancelled orders are excluded — see that separate box below)."
          />
          <CustomCard
            title={
              isDateRangeSelected()
                ? "Payment Received from Budget"
                : "Payment Received from Budget Today"
            }
            value={`₹${formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.order_payment_received_sum || 0
                : overall_stats?.today?.order_payment_received_sum || 0,
            )}`}
            icon={
              <RiseOutlined style={{ fontSize: "26px", color: "#36c6d3" }} />
            }
            bgColor="#fefefe"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.order_payment_received_sum || 0
            }
            description="Amount actually paid so far against confirmed orders created today (not date-filtered on when the payment itself came in — it's the order's current total paid)."
          />
          <CustomCard
            title={
              isDateRangeSelected()
                ? "Total Confirmed Orders"
                : "Total Confirmed Orders Today"
            }
            value={formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.confirmed_order_count || 0
                : overall_stats?.today?.confirmed_order_count || 0,
            )}
            icon={
              <ShoppingCartOutlined
                style={{ fontSize: "26px", color: "#36c6d3" }}
              />
            }
            bgColor="#ed6b75"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.confirmed_order_count || 0
            }
            description="Orders created today excluding Unassigned and Cancelled ones. This is 'Total Orders Today' minus the orders that never became live business."
          />
          <CustomCard
            title={isDateRangeSelected() ? "Refund Amount" : "Refund Amount Today"}
            value={`₹${formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.refund_amount_sum || 0
                : overall_stats?.today?.refund_amount_sum || 0,
            )}`}
            icon={
              <DollarOutlined style={{ fontSize: "26px", color: "#ed6b75" }} />
            }
            bgColor="#ed6b75"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.refund_amount_sum || 0
            }
            description="Refunds approved today (status = accepted), counted by the date the refund was approved — not the date it was originally requested."
          />
          <CustomCard
            title={
              isDateRangeSelected()
                ? "Unassigned & Cancelled Order Budget"
                : "Unassigned & Cancelled Order Budget Today"
            }
            value={`₹${formatNumber(
              isDateRangeSelected()
                ? overall_stats?.custom_range?.unassigned_cancelled_order_budget_sum || 0
                : overall_stats?.today?.unassigned_cancelled_order_budget_sum || 0,
            )}`}
            icon={
              <DollarOutlined style={{ fontSize: "26px", color: "#8c8c8c" }} />
            }
            bgColor="#8c8c8c"
            comparison={
              isDateRangeSelected()
                ? null
                : overall_stats?.yesterday?.unassigned_cancelled_order_budget_sum || 0
            }
            description="Budget of orders created today that ended up Unassigned or Cancelled — the complement of 'Total Order Budget Today', so this quoted value never became a live order."
          />
        </div>
      </Row>

      {/* Business Heads Performance */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: sidebarCollapsed ? "88%" : "79.5%",
          alignItems: "center",
        }}
      >
        <h2 style={{ textAlign: "left" }}>Business Heads Performance</h2>

        <Badge
          size="lg"
          count={` Count : ${hierarchyToRender.length}`}
          color="#36c6d3"
        />
      </div>

      {/* Cards Container with Horizontal Scroll */}
      <div
        style={{
          width: "100%",
          // overflowX: "auto",

          paddingBottom: "8px",
          paddingRight: sidebarCollapsed ? "30px" : "15%",
        }}
      >
        <div
          style={{
            display: "inline-flex", // ✅ inline row layout
            alignItems: "flex-start",
            gap: "16px",
            width: "100vw",
            overflowX: "auto",
            scrollbarWidth: "thin", // Firefox
            WebkitOverflowScrolling: "touch", // smooth scroll on mobile
            paddingBottom: "8px",
            paddingRight: "15%",
          }}
        >
          {hierarchyToRender.map((head) => (
            <Card
              key={head.uid}
              className="business-head-card"
              style={{
                flex: "0 0 auto", // ✅ don’t stretch
                minWidth: isMobile ? "300px" : `500px`, // ✅ fixed width

                minHeight: isDateRangeSelected() ? "auto" : "450px",
                position: "relative",
                zIndex: expanded[head.uid] ? 5 : 1,
                // ✅ prevent card content from creating extra scroll
              }}
              bordered
              title={
                <div
                  style={{
                    padding: "10px 0px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    border: "0px solid white",
                  }}
                >
                  <h3 style={{ margin: 0 }}>{head.user}</h3>
                  <p
                    style={{
                      margin: 0,
                      color: "grey",
                      fontSize: "12px",
                      fontWeight: 400,
                    }}
                  >
                    {head.user_type}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      color: "#36c6d3",
                      fontSize: "12px",
                      fontWeight: 200,
                    }}
                  >
                    {head?.member_count}{" "}
                    {head?.member_count <= 1 ? "Team Member" : "Team Members"}
                  </p>
                </div>
              }
            >
              {isDateRangeSelected() ? (
                <div>
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12}>
                      <div style={{ background: "#f6ffed", padding: "6px" }}>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            fontWeight: 300,
                          }}
                        >
                          Revenue
                        </h4>
                        <strong style={{ fontWeight: 600, color: "#36c6d3" }}>
                          ₹
                          {formatNumber(
                            head?.self_stats?.custom_range?.transaction_sum ||
                            0,
                          )}
                        </strong>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div style={{ background: "#e6f7ff", padding: "6px" }}>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            fontWeight: 300,
                          }}
                        >
                          Orders
                        </h4>
                        <strong style={{ fontWeight: 600, color: "#1C64F2" }}>
                          {formatNumber(
                            head?.self_stats?.custom_range?.task_count || 0,
                          )}
                        </strong>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12}>
                      <div style={{ background: "#fff7e6", padding: "6px" }}>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            fontWeight: 300,
                          }}
                        >
                          New Customers
                        </h4>
                        <strong style={{ fontWeight: 600, color: "#0E9F6E" }}>
                          {formatNumber(
                            head?.self_stats?.custom_range?.client_count || 0,
                          )}
                        </strong>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div style={{ background: "#f9f0ff", padding: "6px" }}>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            fontWeight: 300,
                          }}
                        >
                          New Customer Revenue
                        </h4>
                        <strong style={{ fontWeight: 600, color: "#722ED1" }}>
                          ₹
                          {formatNumber(
                            head?.self_stats?.custom_range
                              ?.transaction_sum_clients_created_in_range || 0,
                          )}
                        </strong>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12}>
                      <div style={{ background: "#CDDBFE", padding: "6px" }}>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            fontWeight: 300,
                          }}
                        >
                          Word Count
                        </h4>
                        <strong style={{ fontWeight: 600, color: "#1C64F2" }}>
                          {formatNumber(
                            head?.self_stats?.custom_range?.word_count || 0,
                          )}
                        </strong>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div style={{ background: "#f0f0f0", padding: "6px" }}>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            fontWeight: 300,
                          }}
                        >
                          Call Connected / Made
                        </h4>
                        <strong style={{ fontWeight: 600, color: "#1C64F2" }}>
                          {formatNumber(
                            head?.self_stats?.custom_range?.call_connected || 0,
                          )} / {" "}
                          {formatNumber(
                            head?.self_stats?.custom_range?.call_made || 0,
                          )}
                        </strong>
                      </div>
                    </Col>
                  </Row>
                </div>
              ) : (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={8}>
                    <div style={{ background: "#f6ffed", padding: "6px" }}>
                      <h4
                        style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                      >
                        Today's Revenue
                      </h4>
                      <strong style={{ fontWeight: 600, color: "#36c6d3" }}>
                        ₹
                        {formatNumber(
                          head?.self_stats?.today?.transaction_sum || 0,
                        )}
                      </strong>
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ background: "#e6f7ff", padding: "6px" }}>
                      <h4
                        style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                      >
                        Orders
                      </h4>
                      <strong style={{ fontWeight: 600, color: "#1C64F2" }}>
                        {formatNumber(head?.self_stats?.today?.task_count || 0)}
                      </strong>
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ background: "#fff7e6", padding: "6px" }}>
                      <h4
                        style={{ margin: 0, fontSize: "12px", fontWeight: 300 }}
                      >
                        New Customers
                      </h4>
                      <strong style={{ fontWeight: 600, color: "#0E9F6E" }}>
                        {formatNumber(
                          head?.self_stats?.today?.client_count || 0,
                        )}
                      </strong>
                    </div>
                  </Col>
                </Row>
              )}

              {isDateRangeSelected() ? null : (
                <Table
                  size="small"
                  pagination={false}
                  columns={columns}
                  className="centered-table"
                  dataSource={[
                    {
                      key: "1",
                      metric: (
                        <span
                          style={{
                            fontWeight: 400,
                            fontSize: 12,
                            textAlign: "left",
                            display: "block",
                          }}
                        >
                          Revenue
                        </span>
                      ),
                      today: (
                        <span>{`₹${formatNumber(
                          head?.self_stats?.today?.transaction_sum || 0,
                        )}`}</span>
                      ),
                      yesterday: (
                        <span>{`₹${formatNumber(
                          head?.self_stats?.yesterday?.transaction_sum || 0,
                        )}`}</span>
                      ),
                      month: (
                        <span style={{ fontWeight: 600, color: "#6875F5" }}>
                          {`₹${formatNumber(
                            head?.self_stats?.this_month?.transaction_sum || 0,
                          )}`}
                        </span>
                      ),
                    },
                    {
                      key: "2",
                      metric: (
                        <span
                          style={{
                            fontWeight: 400,
                            fontSize: 12,
                            textAlign: "left",
                            display: "block",
                          }}
                        >
                          Orders
                        </span>
                      ),
                      today: (
                        <span>
                          {formatNumber(
                            head?.self_stats?.today?.task_count || 0,
                          )}
                        </span>
                      ),
                      yesterday: (
                        <span>
                          {formatNumber(
                            head?.self_stats?.yesterday?.task_count || 0,
                          )}
                        </span>
                      ),
                      month: (
                        <span style={{ fontWeight: 600, color: "#6875F5" }}>
                          {formatNumber(
                            head?.self_stats?.this_month?.task_count || 0,
                          )}
                        </span>
                      ),
                    },
                    {
                      key: "3",
                      metric: (
                        <span
                          style={{
                            fontWeight: 400,
                            fontSize: 12,
                            textAlign: "left",
                            display: "block",
                          }}
                        >
                          New Customer
                        </span>
                      ),
                      today: (
                        <span>
                          {formatNumber(
                            head?.self_stats?.today?.client_count || 0,
                          )}
                        </span>
                      ),
                      yesterday: (
                        <span>
                          {formatNumber(
                            head?.self_stats?.yesterday?.client_count || 0,
                          )}
                        </span>
                      ),
                      month: (
                        <span style={{ fontWeight: 600, color: "#6875F5" }}>
                          {formatNumber(
                            head?.self_stats?.this_month?.client_count || 0,
                          )}
                        </span>
                      ),
                    },
                    {
                      key: "4",
                      metric: (
                        <span
                          style={{
                            fontWeight: 400,
                            fontSize: 12,
                            textAlign: "left",
                            display: "block",
                          }}
                        >
                          New Customer Revenue
                        </span>
                      ),
                      today: (
                        <span>
                          {`₹${formatNumber(
                            head?.self_stats?.today
                              ?.transaction_sum_clients_created_in_range || 0,
                          )}`}
                        </span>
                      ),
                      yesterday: (
                        <span>
                          {`₹${formatNumber(
                            head?.self_stats?.yesterday
                              ?.transaction_sum_clients_created_in_range || 0,
                          )}`}
                        </span>
                      ),
                      month: (
                        <span style={{ fontWeight: 600, color: "#6875F5" }}>
                          {`₹${formatNumber(
                            head?.self_stats?.this_month
                              ?.transaction_sum_clients_created_in_range || 0,
                          )}`}
                        </span>
                      ),
                    },
                    {
                      key: "5",
                      metric: (
                        <span
                          style={{
                            fontWeight: 400,
                            fontSize: 12,
                            textAlign: "left",
                            display: "block",
                          }}
                        >
                          Word Count
                        </span>
                      ),
                      today: (
                        <span>
                          {formatNumber(
                            head?.self_stats?.today?.word_count || 0,
                          )}
                        </span>
                      ),
                      yesterday: (
                        <span>
                          {formatNumber(
                            head?.self_stats?.yesterday?.word_count || 0,
                          )}
                        </span>
                      ),
                      month: (
                        <span style={{ fontWeight: 600, color: "#6875F5" }}>
                          {formatNumber(
                            head?.self_stats?.this_month?.word_count || 0,
                          )}
                        </span>
                      ),
                    },
                  ]}
                />
              )}

              {head.has_more && (
                <Button
                  type="primary"
                  style={{
                    marginTop: 18,
                    width: "100%",
                    background: expanded[head.uid] ? "#e6f7ff" : "#36c6d3",
                    border: "none",
                    color: expanded[head.uid] ? "#36c6d3" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => toggleExpand(head.uid)}
                  loading={loadingExpand[head.uid]}
                  disabled={loadingExpand[head.uid]}
                >
                  {expanded[head.uid] ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      Hide Team Performance{" "}
                      <ChevronUp size={16} style={{ marginLeft: "5px" }} />
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      View Team Performance{" "}
                      <ChevronDown size={16} style={{ marginLeft: "5px" }} />
                    </div>
                  )}
                </Button>
              )}

              {expanded[head.uid] && (
                <div style={{ marginTop: 16 }}>
                  {loadingExpand[head.uid] ? (
                    <Spin />
                  ) : (
                    expanded[head.uid] && (
                      <div
                        style={{
                          maxWidth: "100%",
                          backgroundColor: "#f9f9f9",
                          borderTop: "1px solid #eee",
                          padding: "15px 10px",
                          marginTop: "10px",
                          overflowX: "visible", // Prevent nested scrolling conflict
                        }}
                      >
                        {renderSubTeam(expanded[head.uid])}
                      </div>
                    )
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserAnalytics;
